import React, { useState } from 'react';

function MarketingContact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'deedstack-marketing',
          'subject': 'New DeedStack Inquiry',
          'name': formData.name,
          'email': formData.email,
          'company': formData.company,
          'inquiry-type': formData.subject,
          'message': formData.message
        }).toString()
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', company: '', subject: 'general', message: '' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="marketing-contact">
      {/* Hero */}
      <section className="marketing-hero marketing-hero-short">
        <div className="marketing-hero-content">
          <h1 className="marketing-hero-title">Contact Us</h1>
          <p className="marketing-hero-subtitle">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="marketing-contact-info">
        <div className="container">
          <div className="features-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <div className="feature-card">
              <div className="feature-icon">📧</div>
              <h3>Send a Message</h3>
              <p style={{ color: 'var(--color-text-light)' }}>
                Use the form below to reach us
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-light)' }}>We respond within 1 business day</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">🖥️</div>
              <h3>Try the Demo</h3>
              <p>
                <a href="https://demo.deedstack.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-brand-text)' }}>
                  demo.deedstack.com
                </a>
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-light)' }}>Explore DeedStack™ yourself</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Schedule a Demo</h3>
              <p style={{ color: 'var(--color-text-light)' }}>
                Request a walkthrough
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--color-text-light)' }}>Select "Schedule a Demo" below</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="marketing-contact-form-section">
        <div className="container">
          <h2 className="section-title">Send Us a Message</h2>

          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            {submitted ? (
              <div style={{
                backgroundColor: 'var(--color-success-bg)',
                border: '2px solid var(--color-success-border)',
                borderRadius: '10px',
                padding: '2rem',
                textAlign: 'center',
                color: 'var(--color-success-text)'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h3 style={{ marginBottom: '0.5rem' }}>Thank You!</h3>
                <p style={{ marginBottom: '1rem' }}>
                  Your message has been received. We'll get back to you within 1 business day.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn btn-secondary"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                name="deedstack-marketing"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="contact-form"
              >
                <input type="hidden" name="form-name" value="deedstack-marketing" />
                <input type="hidden" name="subject" value="New DeedStack Inquiry" />
                <input type="hidden" name="bot-field" />

                {error && (
                  <div style={{
                    backgroundColor: 'var(--color-error-bg)',
                    border: '1px solid var(--color-error-border)',
                    borderRadius: '5px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    color: 'var(--color-error-text)'
                  }}>
                    {error}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">Company Name</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="inquiry-type">Subject *</label>
                    <select
                      id="inquiry-type"
                      name="inquiry-type"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                    >
                      <option value="general">General Inquiry</option>
                      <option value="sales">Sales Question</option>
                      <option value="demo">Schedule a Demo</option>
                      <option value="support">Technical Support</option>
                      <option value="partnership">Partnership Opportunity</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows="6"
                    placeholder="Tell us about your land financing business and how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default MarketingContact;
